import {
  DEFAULT_REPORT_COLUMN_IDS,
  DEFAULT_REPORT_SORTS,
  MAXIMUM_VIDEO_SELECTIONS,
  MINIMUM_VIDEO_COMPARISON_SELECTIONS,
  materializeReportFilterDateRange,
  serializeReportFilterUrl,
  parseVideoSelectionUrl,
  type ComparisonResponse,
  type ReportColumnId,
  type ReportFilterStateV1,
  type ReportSort,
  type ReportingErrorCode,
  type VideoMetricRow,
  type VideoReportResponse,
  type VideoSelection,
} from "@meta-video/contracts";
import {
  Alert,
  Button,
  CloseButton,
  FocusTrap,
  Group,
  Loader,
  Stack,
  Text,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  keepPreviousData,
  useQuery,
} from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { restoreFocus } from "../../app/a11y.js";
import {
  AccessDeniedError,
  PROTECTED_QUERY_KEY,
  SessionExpiredError,
} from "../../shared/api/client.js";
import {
  useAnalytics,
  useAnalyticsSessionId,
} from "../../shared/analytics/AnalyticsProvider.js";
import { analyticsEvents } from "../../shared/analytics/events.js";
import { useProductAnalytics } from "../../shared/analytics/useProductAnalytics.js";
import { ComparisonView } from "../comparison/ComparisonView.js";
import { useComparisonUrlState } from "../comparison/useComparisonUrlState.js";
import { AdNameSearchInput } from "../filters/AdNameSearchInput.js";
import { VideoPlaybackModal } from "../video/VideoPlaybackModal.js";
import {
  VideoDetailHeader,
  VideoDetailView,
} from "../video/VideoDetailView.js";
import { videoApi, type VideoApi } from "../video/api.js";
import {
  parseReportSortSearch,
  serializeReportSortSearch,
} from "./sortUrlState.js";
import { useVideoUrlState } from "../video/useVideoUrlState.js";
import {
  reportApi,
  ReportingApiError,
  type ReportApi,
} from "./api.js";
import { ColumnChooser } from "./ColumnChooser.js";
import {
  hiddenReportColumnPreferences,
  isVideoCoverageMetric,
  visibleReportColumnIds,
} from "./columns.js";
import { ReportTable } from "./ReportTable.js";
import { SelectedVideoPanel } from "./SelectedVideoPanel.js";
import { reportingEnvelopeFingerprint } from "./reportingEnvelope.js";

const PAGE_SIZE = 100;
const REPORT_ERROR_TITLES: Readonly<Record<ReportingErrorCode, string>> = {
  REPORTING_BUDGET_EXCEEDED: "Report exceeds the safe query budget",
  REPORTING_QUOTA_EXCEEDED: "Too many report queries",
  REPORTING_REQUEST_INVALID: "Report filters were rejected",
  REPORTING_TIMEOUT: "Report query timed out",
  REPORTING_UNAVAILABLE: "Reporting is temporarily unavailable",
};

export interface ReportWorkspaceProps {
  readonly api?: ReportApi;
  readonly filters: ReportFilterStateV1;
  readonly now?: () => Date;
  readonly onAccessChanged?: () => void;
  readonly onFiltersChange: (filters: ReportFilterStateV1) => boolean | void;
  readonly onSessionExpired: () => void;
  readonly onUnknownDurationCount?: (count: number | undefined) => void;
  readonly playbackApi?: VideoApi;
  readonly selectedVideo?: VideoSelection | null;
  readonly onSelectedVideoChange?: (selection: VideoSelection | null) => void;
}

interface PaginationState {
  readonly filterKey: string;
  readonly offset: number;
}

interface ReportSnapshot {
  readonly filterKey: string;
  readonly offset: number;
  readonly report: VideoReportResponse;
  readonly requestFilters: ReportFilterStateV1;
  readonly sorts: readonly ReportSort[];
}

interface CoherentComparisonSnapshot {
  readonly identityKey: string;
  readonly requestKey: string;
  readonly response: ComparisonResponse;
}

interface PlaybackState {
  readonly row: VideoMetricRow;
  readonly videoMetricCoverageThreshold: number;
}

interface ComparisonIssue {
  readonly description: string;
  readonly kind: "error" | "mismatch";
  readonly retry: () => void;
  readonly retryLabel: string;
  readonly title: string;
}

export function ReportWorkspace({
  api = reportApi,
  filters,
  now = systemNow,
  onAccessChanged,
  onFiltersChange,
  onSessionExpired,
  onUnknownDurationCount,
  playbackApi = videoApi,
  selectedVideo: controlledSelectedVideo,
  onSelectedVideoChange,
}: ReportWorkspaceProps) {
  const analytics = useAnalytics();
  const analyticsSessionId = useAnalyticsSessionId();
  const productAnalytics = useProductAnalytics();
  const desktopWatch = useMediaQuery("(min-width: 80em)");
  const clock = useUtcDayClock(now);
  const requestFilters = useMemo<ReportFilterStateV1>(
    () => materializeReportRequestFilters(filters, clock),
    [clock, filters],
  );
  const filterKey = serializeReportFilterUrl(requestFilters);
  const [pagination, setPagination] = useState<PaginationState>({
    filterKey,
    offset: 0,
  });
  const offset = pagination.filterKey === filterKey ? pagination.offset : 0;
  const [sorts, setSorts] = useState<readonly ReportSort[]>(
    () => parseReportSortSearch(window.location.search),
  );
  const [columnIds, setColumnIds] = useState<readonly ReportColumnId[]>(
    DEFAULT_REPORT_COLUMN_IDS,
  );
  const [preferenceError, setPreferenceError] = useState<unknown>(null);
  const [preferenceWrites, setPreferenceWrites] = useState(0);
  const comparisonUrl = useComparisonUrlState();
  const videoUrl = useVideoUrlState();
  const comparisonTriggerRef = useRef<HTMLButtonElement>(null);
  const playbackReturnFocusRef = useRef<HTMLElement | null>(null);
  const reportWorkspaceRef = useRef<HTMLElement>(null);
  const [playback, setPlayback] = useState<PlaybackState | null>(null);
  const [playbackOpened, setPlaybackOpened] = useState(false);
  const selectedVideo = controlledSelectedVideo === undefined
    ? videoUrl.selection
    : controlledSelectedVideo;
  const [inspectOpened, setInspectOpened] = useState(false);
  const [selectedVideoCollapsed, setSelectedVideoCollapsed] = useState(false);
  const inspectTriggerRef = useRef<HTMLButtonElement | null>(null);
  const selectedVideoRegionId = "report-selected-video";
  const selectedVideoDialogTitleId = `${selectedVideoRegionId}-dialog-title`;
  const preferenceEdited = useRef(false);
  const hiddenPreferenceColumnIds = useRef<readonly ReportColumnId[]>([]);
  const preferenceTail = useRef<Promise<unknown>>(Promise.resolve());
  const preferenceSequence = useRef(0);
  const preferenceControllers = useRef(new Set<AbortController>());
  const mounted = useRef(true);
  const lastReport = useRef<ReportSnapshot | null>(null);
  const lastComparison = useRef<CoherentComparisonSnapshot | null>(null);
  const detailWasOpen = useRef(false);
  const comparisonWasOpen = useRef(false);
  const detailAnalyticsSessionId = useRef<string | null>(null);
  const comparisonAnalyticsSessionId = useRef<string | null>(null);
  const desktopWatchWasEnabled = useRef(desktopWatch);

  useEffect(() => {
    if (pagination.filterKey !== filterKey) {
      setPagination({ filterKey, offset: 0 });
    }
  }, [filterKey, pagination.filterKey]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      for (const controller of preferenceControllers.current) controller.abort();
      preferenceControllers.current.clear();
    };
  }, []);

  const preferences = useQuery({
    queryFn: ({ signal }) => api.getPreferences({ signal }),
    queryKey: [...PROTECTED_QUERY_KEY, "report", "preferences", api],
  });
  useEffect(() => {
    if (preferences.data && !preferenceEdited.current) {
      hiddenPreferenceColumnIds.current = hiddenReportColumnPreferences(
        preferences.data.columnIds,
      );
      const visibleColumns = visibleReportColumnIds(preferences.data.columnIds);
      const nextColumns = visibleColumns.length > 0 ? visibleColumns : DEFAULT_REPORT_COLUMN_IDS;
      setColumnIds((currentColumns) => currentColumns.length === nextColumns.length
        && currentColumns.every((columnId, index) => columnId === nextColumns[index])
        ? currentColumns
        : nextColumns);
    }
  }, [preferences.data]);

  const report = useQuery({
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => api.getReport(
      { filters: requestFilters, limit: PAGE_SIZE, offset, sorts },
      { signal },
    ),
    queryKey: [
      ...PROTECTED_QUERY_KEY,
      "report",
      "videos",
      filterKey,
      offset,
      sorts,
      api,
    ],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const currentSnapshot: ReportSnapshot | null = report.data !== undefined
    && !report.isPlaceholderData
    && !report.isFetching
    && report.error === null
    ? {
        filterKey,
        offset,
        report: report.data!,
        requestFilters,
        sorts,
      }
    : null;
  const updating = report.isFetching;
  useEffect(() => {
    if (currentSnapshot !== null) lastReport.current = currentSnapshot;
  }, [currentSnapshot]);
  const snapshot = currentSnapshot !== null
    ? currentSnapshot
    : updating ? lastReport.current : null;
  const selectedBrandIds = new Set(
    (currentSnapshot?.requestFilters ?? requestFilters).brandIds,
  );
  const comparisonSelections = comparisonUrl.selections.filter(
    ({ brandId }) => selectedBrandIds.has(brandId),
  );
  const hasDroppedComparisonSelections = comparisonSelections.length
    !== comparisonUrl.selections.length;
  const comparisonExpectedFingerprint = snapshot === null
    ? null
    : reportingEnvelopeFingerprint(snapshot.report);
  const comparisonIdentityKey = JSON.stringify(comparisonSelections);
  const comparisonRequestKey = snapshot === null || comparisonExpectedFingerprint === null
    ? null
    : JSON.stringify({
        filterKey: snapshot.filterKey,
        identities: comparisonIdentityKey,
        snapshot: comparisonExpectedFingerprint,
      });
  const comparisonOpened = comparisonUrl.isOpen
    && comparisonSelections.length >= MINIMUM_VIDEO_COMPARISON_SELECTIONS;
  const comparisonEnabled = comparisonOpened
    && currentSnapshot !== null;
  const comparison = useQuery({
    enabled: comparisonEnabled,
    queryFn: ({ signal }) => {
      if (snapshot === null) {
        return Promise.reject(new Error("Comparison snapshot is not ready"));
      }
      return api.getComparison(
        {
          filters: snapshot.requestFilters,
          videos: comparisonSelections,
        },
        { signal },
      );
    },
    queryKey: [
      ...PROTECTED_QUERY_KEY,
      "report",
      "comparison",
      comparisonRequestKey ?? "pending",
      api,
    ],
    refetchOnWindowFocus: false,
  });
  const comparisonOutOfSync = comparison.data !== undefined
    && comparisonExpectedFingerprint !== null
    && reportingEnvelopeFingerprint(comparison.data) !== comparisonExpectedFingerprint;
  const currentComparisonSnapshot: CoherentComparisonSnapshot | null =
    comparison.data !== undefined
    && currentSnapshot !== null
    && comparisonRequestKey !== null
    && !comparisonOutOfSync
    && !comparison.isFetching
    && comparison.error === null
      ? {
          identityKey: comparisonIdentityKey,
          requestKey: comparisonRequestKey,
          response: comparison.data,
        }
      : null;
  // Persisting a column choice is cosmetic and is reported locally by the
  // "Column preferences were not saved" alert. A rejected write must not enter
  // the authorization path: onAccessChanged clears every protected query and,
  // on a second denial at one permission version, replaces the application
  // with the router's full-page "Report access changed" takeover. Genuine loss
  // of report access still surfaces through the reads below.
  const accessDenied = [
    preferences.error,
    report.error,
    comparison.error,
  ]
    .some((error) => error instanceof AccessDeniedError);
  const priorComparison = lastComparison.current;
  const priorMatchesIdentities = priorComparison?.identityKey === comparisonIdentityKey;
  const displayedComparison = accessDenied
    ? null
    : currentComparisonSnapshot ?? (
        priorMatchesIdentities
          ? priorComparison
          : null
      );
  const showingPreviousComparison = displayedComparison !== null
    && currentComparisonSnapshot === null;

  useEffect(() => {
    if (currentComparisonSnapshot !== null) {
      lastComparison.current = currentComparisonSnapshot;
    }
  }, [currentComparisonSnapshot]);

  const expired = [
    preferences.error,
    report.error,
    comparison.error,
    preferenceError,
  ].some((error) => error instanceof SessionExpiredError);
  useEffect(() => {
    if (expired) onSessionExpired();
  }, [expired, onSessionExpired]);
  useLayoutEffect(() => {
    if (!accessDenied) return;
    lastReport.current = null;
    lastComparison.current = null;
    setPlayback(null);
    setPlaybackOpened(false);
    onAccessChanged?.();
  }, [accessDenied, onAccessChanged]);
  const displayReport = accessDenied ? undefined : snapshot?.report;
  const selectedRow = displayReport?.rows.find((row) =>
    row.brandId === selectedVideo?.brandId && row.videoId === selectedVideo.videoId,
  ) ?? null;
  const hideCoverageMetrics = displayReport !== undefined && (
    displayReport.total.videoCoverageRate === null
    || displayReport.total.videoCoverageRate < displayReport.coverage.videoMetricThreshold
  );

  useEffect(() => {
    const wasOpen = detailWasOpen.current;
    detailWasOpen.current = videoUrl.isDetailOpen;
    if (videoUrl.isDetailOpen) {
      setInspectOpened(false);
      setPlaybackOpened(false);
      return;
    }
    if (!wasOpen) return;
    const focusedRow = reportWorkspaceRef.current?.querySelector<HTMLElement>(
      "tr[aria-current='true']",
    ) ?? null;
    window.requestAnimationFrame(() => restoreFocus(
      focusedRow,
      reportWorkspaceRef.current,
    ));
  }, [videoUrl.isDetailOpen]);

  useEffect(() => {
    if (!analyticsSessionId || !videoUrl.isDetailOpen) {
      detailAnalyticsSessionId.current = null;
      return;
    }
    if (detailAnalyticsSessionId.current === analyticsSessionId) return;
    detailAnalyticsSessionId.current = analyticsSessionId;
    productAnalytics.reportInteraction({
      action: "opened",
      feature: "detail",
      surface: "report",
    });
  }, [analyticsSessionId, productAnalytics, videoUrl.isDetailOpen]);

  useEffect(() => {
    const wasOpen = comparisonWasOpen.current;
    comparisonWasOpen.current = comparisonOpened;
    if (comparisonOpened || !wasOpen) return;
    window.requestAnimationFrame(() => restoreFocus(
      comparisonTriggerRef.current,
      reportWorkspaceRef.current,
    ));
  }, [comparisonOpened]);

  useEffect(() => {
    if (!analyticsSessionId || !comparisonOpened) {
      comparisonAnalyticsSessionId.current = null;
      return;
    }
    if (comparisonAnalyticsSessionId.current === analyticsSessionId) return;
    comparisonAnalyticsSessionId.current = analyticsSessionId;
    analytics.capture(
      analyticsEvents.comparisonViewed(analyticsSessionId, {
        selectedCount: comparisonSelections.length,
        videos: comparisonSelections,
      }),
    );
  }, [analytics, analyticsSessionId, comparisonOpened, comparisonSelections]);

  useEffect(() => {
    const wasDesktop = desktopWatchWasEnabled.current;
    desktopWatchWasEnabled.current = desktopWatch;
    if (wasDesktop === desktopWatch || videoUrl.isDetailOpen || comparisonOpened) return;
    if (desktopWatch) {
      if (!inspectOpened) return;
      window.requestAnimationFrame(() => restoreFocus(
        document.getElementById(selectedVideoRegionId),
        reportWorkspaceRef.current,
      ));
      return;
    }
    if (selectedVideo !== null) {
      setInspectOpened(true);
    }
  }, [comparisonOpened, desktopWatch, inspectOpened, selectedVideo, videoUrl.isDetailOpen]);

  useLayoutEffect(() => {
    if (
      desktopWatch
      && selectedVideo !== null
      && !videoUrl.isDetailOpen
      && !comparisonOpened
    ) {
      setInspectOpened(true);
    }
  }, [comparisonOpened, desktopWatch, selectedVideo, videoUrl.isDetailOpen]);

  useEffect(() => {
    if (desktopWatch || !inspectOpened || videoUrl.isDetailOpen || comparisonOpened) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [comparisonOpened, desktopWatch, inspectOpened, videoUrl.isDetailOpen]);

  // Mirror the sort into the URL so a remount restores it. Replace rather than
  // push: re-sorting is a refinement of the current view, not a new one.
  useEffect(() => {
    const next = serializeReportSortSearch(sorts, window.location.search);
    if (next === window.location.search.replace(/^\?/, "")) return;
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${next === "" ? "" : `?${next}`}${window.location.hash}`,
    );
  }, [sorts]);

  useEffect(() => {
    if (!hideCoverageMetrics) return;
    if (sorts.some(({ metric }) => isVideoCoverageMetric(metric))) {
      setSorts(DEFAULT_REPORT_SORTS);
      setPagination({ filterKey, offset: 0 });
    }
  }, [filterKey, hideCoverageMetrics, sorts]);

  useEffect(() => {
    const total = report.data?.total.videoCount;
    if (total === undefined || offset === 0 || offset < total) return;
    const clamped = total === 0 ? 0 : Math.floor((total - 1) / PAGE_SIZE) * PAGE_SIZE;
    setPagination({ filterKey, offset: clamped });
  }, [filterKey, offset, report.data?.total.videoCount]);

  useEffect(() => {
    const count = snapshot?.filterKey === filterKey
      ? displayReport?.total.unknownDurationVideoCount
      : undefined;
    onUnknownDurationCount?.(count);
  }, [displayReport?.total.unknownDurationVideoCount, filterKey, onUnknownDurationCount, snapshot?.filterKey]);

  const saveColumns = useCallback((next: readonly ReportColumnId[]) => {
    preferenceEdited.current = true;
    setColumnIds(next);
    setPreferenceError(null);
    const sequence = ++preferenceSequence.current;
    const controller = new AbortController();
    preferenceControllers.current.add(controller);
    setPreferenceWrites((count) => count + 1);
    const persistedColumnIds = [
      ...next,
      ...hiddenPreferenceColumnIds.current.filter(
        (columnId) => !next.includes(columnId),
      ),
    ];
    preferenceTail.current = preferenceTail.current
      .catch(() => undefined)
      .then(() => api.putPreferences(
        { columnIds: persistedColumnIds },
        { signal: controller.signal },
      ))
      .then(() => {
        productAnalytics.reportInteraction({
          action: "changed",
          feature: "column",
          state: next.length >= columnIds.length ? "enabled" : "disabled",
          surface: "report",
        });
        if (mounted.current && sequence === preferenceSequence.current) {
          setPreferenceError(null);
        }
      })
      .catch((error: unknown) => {
        if (mounted.current && !(error instanceof DOMException && error.name === "AbortError")) {
          setPreferenceError(error);
        }
      })
      .finally(() => {
        preferenceControllers.current.delete(controller);
        if (mounted.current) setPreferenceWrites((count) => Math.max(0, count - 1));
      });
  }, [api, columnIds.length, productAnalytics]);

  const onSortChange = useCallback((next: readonly ReportSort[]) => {
    setSorts(next);
    setPagination({ filterKey, offset: 0 });
    productAnalytics.reportInteraction({
      action: "changed",
      feature: "sort",
      state: next[0]?.direction === "asc" ? "ascending" : "descending",
      surface: "report",
    });
  }, [filterKey, productAnalytics]);

  const updateComparisonSelections = useCallback((
    action: "add" | "clear" | "remove",
    nextSelections: readonly VideoSelection[],
  ): boolean => {
    if (!comparisonUrl.setSelections(nextSelections)) return false;
    if (analyticsSessionId) {
      analytics.capture(
        analyticsEvents.comparisonSelectionChanged(analyticsSessionId, {
          action,
          selectedCount: nextSelections.length,
          videos: nextSelections,
        }),
      );
    }
    return true;
  }, [analytics, analyticsSessionId, comparisonUrl]);

  const toggleComparisonVideo = useCallback((selection: VideoSelection) => {
    const selected = comparisonSelections.some(
      (candidate) => candidate.brandId === selection.brandId
        && candidate.videoId === selection.videoId,
    );
    const nextSelections = selected
      ? comparisonSelections.filter(
            (candidate) => candidate.brandId !== selection.brandId
              || candidate.videoId !== selection.videoId,
          )
      : [...comparisonSelections, selection];
    updateComparisonSelections(selected ? "remove" : "add", nextSelections);
  }, [comparisonSelections, updateComparisonSelections]);

  const clearComparisonSelections = useCallback(() => {
    if (comparisonUrl.selections.length === 0) return;
    updateComparisonSelections("clear", []);
  }, [comparisonUrl.selections.length, updateComparisonSelections]);

  const selectVideo = useCallback((selection: VideoSelection) => {
    videoUrl.focusVideo(selection);
    onSelectedVideoChange?.(selection);
  }, [onSelectedVideoChange, videoUrl]);

  const openInspect = useCallback((selection: VideoSelection, trigger: HTMLButtonElement) => {
    selectVideo(selection);
    inspectTriggerRef.current = trigger;
    setInspectOpened(true);
  }, [selectVideo]);

  const closeInspect = useCallback(() => {
    setInspectOpened(false);
    window.requestAnimationFrame(() => restoreFocus(
      inspectTriggerRef.current,
      reportWorkspaceRef.current,
    ));
  }, []);

  const addFocusedVideoToComparison = useCallback((selection: VideoSelection) => {
    const alreadySelected = comparisonSelections.some(
      (candidate) => candidate.brandId === selection.brandId
        && candidate.videoId === selection.videoId,
    );
    if (
      !alreadySelected &&
      !updateComparisonSelections("add", [...comparisonSelections, selection])
    ) return;
    const installed = parseVideoSelectionUrl(window.location.search);
    if (
      installed.kind !== "valid"
      || !installed.selections.some((candidate) =>
        candidate.brandId === selection.brandId
        && candidate.videoId === selection.videoId)
    ) return;
    videoUrl.focusVideo(selection);
  }, [comparisonSelections, updateComparisonSelections, videoUrl]);

  const reportError = report.error;
  const hasDisplayedComparison = displayedComparison !== null;
  const reportErrorRecovery = reportError === null
    ? null
    : getReportErrorRecovery(reportError, filters, clock);
  const retryReportContext = () => {
    void report.refetch();
  };
  const retainedDetailSnapshot = videoUrl.isDetailOpen
    ? accessDenied ? null : currentSnapshot ?? lastReport.current
    : null;
  const narrowerComparisonFilters = reportErrorRecovery?.narrowedFilters ?? null;
  let comparisonIssue: ComparisonIssue | null = null;

  if (reportError !== null) {
    comparisonIssue = {
      description: `${hasDisplayedComparison
        ? "The replacement report request failed. The exact previous comparison remains visible but inactive."
        : "The report request failed, so no partial comparison metrics are shown."} ${reportErrorRecovery!.description}`,
      kind: "error",
      retry: narrowerComparisonFilters === null
        ? retryReportContext
        : () => onFiltersChange(narrowerComparisonFilters),
      retryLabel: narrowerComparisonFilters === null
        ? "Retry report context"
        : reportErrorRecovery!.actionLabel,
      title: "Report context could not be updated",
    };
  } else if (comparisonOutOfSync) {
    comparisonIssue = {
      description: hasDisplayedComparison
        ? "The mismatched replacement is hidden. The exact previous comparison remains visible but inactive."
        : "The comparison and report resolved against different snapshots, so no partial comparison metrics are shown.",
      kind: "mismatch",
      retry: () => void comparison.refetch(),
      retryLabel: "Reload comparison",
      title: "Video comparison is out of sync",
    };
  } else if (comparison.error !== null) {
    comparisonIssue = {
      description: hasDisplayedComparison
        ? "The replacement comparison failed. The exact previous comparison remains visible but inactive."
        : "The comparison request failed. No partial comparison metrics are shown.",
      kind: "error",
      retry: () => void comparison.refetch(),
      retryLabel: "Reload comparison",
      title: "Video comparison could not be loaded",
    };
  }
  const reportContextIssue = reportErrorRecovery !== null ? (
    <ReportErrorPanel
      onFiltersChange={onFiltersChange}
      onRetry={retryReportContext}
      recovery={reportErrorRecovery}
    />
  ) : null;
  const unavailableColumns: readonly ReportColumnId[] = hideCoverageMetrics
    ? ["hookRate", "holdRate"]
    : [];
  const selectedVideoDialogOpened = !desktopWatch && inspectOpened;
  const selectedVideoSurfaceVisible = desktopWatch || inspectOpened;

  return (
    <section
      aria-busy={updating}
      aria-label="Report results"
      className={`report-workspace${updating && snapshot ? " report-workspace--updating" : ""}${selectedVideoDialogOpened ? " report-workspace--inspect-open" : ""}`}
      data-stale={updating && snapshot ? "true" : "false"}
      ref={reportWorkspaceRef}
      role="region"
      tabIndex={-1}
    >
      <Stack gap="sm">
        {comparisonOpened ? (
          <ComparisonView
            api={playbackApi}
            disabled={showingPreviousComparison || comparisonIssue !== null}
            error={comparisonIssue?.kind === "error" ? true : null}
            errorDescription={comparisonIssue?.description}
            errorTitle={comparisonIssue?.title}
            isLoading={comparisonIssue === null
              && displayedComparison === null
              && (snapshot === null || comparison.isPending)}
            isUpdating={showingPreviousComparison
              && comparisonIssue === null
              && (updating || comparison.isFetching || comparison.isPending)}
            onAccessChanged={onAccessChanged}
            onAddVideo={comparisonUrl.closeComparison}
            onClear={clearComparisonSelections}
            onClose={comparisonUrl.closeComparison}
            onRetry={comparisonIssue?.retry}
            onSessionExpired={onSessionExpired}
            opened
            outOfSync={comparisonIssue?.kind === "mismatch"}
            outOfSyncDescription={comparisonIssue?.description}
            outOfSyncTitle={comparisonIssue?.title}
            response={displayedComparison?.response}
            retryLabel={comparisonIssue?.retryLabel}
            selections={comparisonSelections}
            showingPrevious={showingPreviousComparison}
          />
        ) : (
          <>
        {!videoUrl.isDetailOpen ? <>
        {preferences.error && !(preferences.error instanceof SessionExpiredError) ? (
          <Alert color="orange" title="Column preferences could not be loaded">
            The stated default columns are in use for this session.
          </Alert>
        ) : null}
        {preferenceError && !(preferenceError instanceof SessionExpiredError) ? (
          <Alert color="orange" title="Column preferences were not saved">
            Your selection is active for this page, but could not be saved across sessions.
          </Alert>
        ) : null}
        </> : null}

        {report.isPending ? (
          <div aria-live="polite" className="report-loading" role="status">
            <Loader size="sm" /> Loading report…
          </div>
        ) : null}
        {updating && snapshot && !videoUrl.isDetailOpen ? (
          <div aria-label="Updating report" aria-live="polite" className="report-updating" role="status">
            <Loader size="xs" /> Updating report… previous results remain visible.
          </div>
        ) : null}

        {videoUrl.isDetailOpen && selectedVideo ? (
          accessDenied ? (
            <VideoDetailContextFallback
              issue={(
                <Text aria-label="Report access changed" role="status" size="sm">
                  Report access changed. Rechecking your permitted workspace…
                </Text>
              )}
              onBack={videoUrl.closeDetail}
              selection={selectedVideo}
            />
          ) :
          retainedDetailSnapshot ? (
            <VideoDetailView
              api={api}
              contextIssue={reportContextIssue}
              contextUpdating={updating || reportContextIssue !== null}
              expectedFingerprint={reportingEnvelopeFingerprint(retainedDetailSnapshot.report)}
              filters={retainedDetailSnapshot.requestFilters}
              onAccessChanged={onAccessChanged}
              onAddToComparison={addFocusedVideoToComparison}
              onBack={videoUrl.closeDetail}
              onSessionExpired={onSessionExpired}
              playbackApi={playbackApi}
              selection={selectedVideo}
            />
          ) : (
            <VideoDetailContextFallback
              issue={reportContextIssue}
              onBack={videoUrl.closeDetail}
              selection={selectedVideo}
            />
          )
        ) : accessDenied ? (
          <Text aria-label="Report access changed" role="status" size="sm">
            Report access changed. Rechecking your permitted workspace…
          </Text>
        ) : reportContextIssue !== null ? (
          reportContextIssue
        ) : displayReport && snapshot ? (
          <>
            {displayReport.cacheStatus === "stale" ? (
              <Alert
                aria-label="Cached results may be out of date"
                color="orange"
                role="alert"
                title="Cached results may be out of date"
              >
                Freshness checks are unavailable, so these complete cached results may not include the latest data.
              </Alert>
            ) : null}
            <div className={desktopWatch
              ? `report-workspace__layout report-workspace__layout--with-watch${selectedVideoCollapsed
                ? " report-workspace__layout--watch-collapsed"
                : ""}`
              : "report-workspace__layout"}>
              <div className="report-workspace__primary">
                <Group className="comparison-toolbar" justify="space-between" wrap="wrap">
                  <Group
                    className="comparison-toolbar__selection-context"
                    gap="xs"
                    wrap="nowrap"
                  >
                    <AdNameSearchInput
                      className="comparison-toolbar__name-search"
                      filters={filters}
                      onChange={onFiltersChange}
                      placeholder="Ad name contains"
                      size="xs"
                    />
                    <Text className="comparison-toolbar__selection-status" c="dimmed" size="sm">
                      {comparisonSelections.length === 1
                        ? "1 video selected"
                        : `${comparisonSelections.length} videos selected`} · choose {MINIMUM_VIDEO_COMPARISON_SELECTIONS}–{MAXIMUM_VIDEO_SELECTIONS}
                    </Text>
                  </Group>
                  <Group gap="xs">
                    <Button
                      disabled={updating
                        || comparisonSelections.length < MINIMUM_VIDEO_COMPARISON_SELECTIONS}
                      onClick={comparisonUrl.openComparison}
                      ref={comparisonTriggerRef}
                      size="compact-sm"
                    >
                      Compare ({comparisonSelections.length})
                    </Button>
                    <Button
                      disabled={updating || comparisonUrl.selections.length === 0}
                      onClick={clearComparisonSelections}
                      size="compact-sm"
                      variant="default"
                    >
                      Clear selection
                    </Button>
                    <ColumnChooser
                      columnIds={columnIds}
                      isSaving={preferenceWrites > 0}
                      onChange={saveColumns}
                      unavailableColumnIds={unavailableColumns}
                    />
                  </Group>
                </Group>
                {displayReport.rows.length === 0
                  && displayReport.total.videoCount > 0
                  && snapshot.offset > 0 ? (
                  <div aria-live="polite" role="status">Returning to the last available report page…</div>
                ) : displayReport.rows.length === 0 ? (
                  <Alert color="blue" role="status" title="No rows matched your filters">
                    The query completed successfully. Broaden the filters to see videos.
                  </Alert>
                ) : (
                  <>
                    <ReportTable
                      api={api}
                      columnIds={columnIds}
                      disabled={updating}
                      expectedSnapshotFingerprint={reportingEnvelopeFingerprint(snapshot.report)}
                      filters={snapshot.requestFilters}
                      focusedRegionId={desktopWatch ? selectedVideoRegionId : undefined}
                      focusedVideo={selectedVideo}
                      key={`${snapshot.filterKey}\0${snapshot.offset}`}
                      onAccessChanged={onAccessChanged}
                      onFocusVideo={selectVideo}
                      onInspectVideo={desktopWatch ? undefined : openInspect}
                      onPlay={(row, trigger) => {
                        playbackReturnFocusRef.current = trigger;
                        setPlayback({
                          row,
                          videoMetricCoverageThreshold: displayReport.coverage.videoMetricThreshold,
                        });
                        setPlaybackOpened(true);
                      }}
                      onSessionExpired={onSessionExpired}
                      onSortChange={onSortChange}
                      onToggleVideo={toggleComparisonVideo}
                      rows={displayReport.rows}
                      selectedVideos={comparisonSelections}
                      sorts={snapshot.sorts}
                      total={displayReport.total}
                      videoMetricCoverageThreshold={displayReport.coverage.videoMetricThreshold}
                    />
                    <Pagination
                      disabled={updating}
                      offset={snapshot.offset}
                      onOffsetChange={(nextOffset) => setPagination({ filterKey, offset: nextOffset })}
                      rowCount={displayReport.rows.length}
                      total={displayReport.total.videoCount}
                    />
                  </>
                )}
              </div>
              <div
                aria-hidden="true"
                className="report-workspace__inspect-overlay"
                hidden={!selectedVideoDialogOpened}
                onClick={closeInspect}
              />
              {selectedVideoSurfaceVisible ? (
                <FocusTrap active={selectedVideoDialogOpened}>
                  <aside
                    aria-labelledby={selectedVideoDialogOpened
                      ? selectedVideoDialogTitleId
                      : undefined}
                    aria-modal={selectedVideoDialogOpened ? true : undefined}
                    className={`report-workspace__watch${selectedVideoDialogOpened
                      ? " report-workspace__watch--dialog"
                      : ""}${desktopWatch && selectedVideoCollapsed
                      ? " report-workspace__watch--collapsed"
                      : ""}`}
                    onKeyDown={(event) => {
                      if (selectedVideoDialogOpened && event.key === "Escape") {
                        event.stopPropagation();
                        closeInspect();
                      }
                    }}
                    role={selectedVideoDialogOpened ? "dialog" : undefined}
                  >
                    <div className="report-workspace__inspect-header">
                      <Text component="h2" fw={700} id={selectedVideoDialogTitleId}>
                        Inspect video
                      </Text>
                      <CloseButton
                        aria-label="Close selected video"
                        data-autofocus
                        onClick={closeInspect}
                      />
                    </div>
                    <SelectedVideoPanel
                      api={playbackApi}
                      collapsed={desktopWatch && selectedVideoCollapsed}
                      disabled={updating}
                      id={selectedVideoRegionId}
                      loading={updating}
                      onAccessChanged={onAccessChanged}
                      onAddToComparison={addFocusedVideoToComparison}
                      onCollapsedChange={desktopWatch ? setSelectedVideoCollapsed : undefined}
                      onSessionExpired={onSessionExpired}
                      onInspect={videoUrl.openDetail}
                      row={selectedRow}
                      selection={selectedVideo}
                      videoMetricCoverageThreshold={displayReport.coverage.videoMetricThreshold}
                    />
                  </aside>
                </FocusTrap>
              ) : null}
            </div>
          </>
        ) : null}
          </>
        )}
        {comparisonUrl.error ? (
          <Alert color="red" role="alert">
            {comparisonUrl.error} The comparison is closed until the shared URL is corrected.
          </Alert>
        ) : null}
        {videoUrl.error ? (
          <Alert color="orange" role="alert">
            {videoUrl.error} Video detail is closed until a valid video is selected.
          </Alert>
        ) : null}
        {hasDroppedComparisonSelections ? (
          <Alert color="orange" role="alert">
            Some shared video selections are outside the active brands and are not included.
          </Alert>
        ) : null}
      </Stack>
      {playback && !accessDenied && !comparisonOpened && !videoUrl.isDetailOpen ? (
        <VideoPlaybackModal
          api={playbackApi}
          fallbackFocusRef={reportWorkspaceRef}
          onAccessChanged={onAccessChanged}
          onClose={() => setPlaybackOpened(false)}
          onSessionExpired={onSessionExpired}
          opened={playbackOpened}
          key={`${playback.row.brandId}\0${playback.row.videoId}`}
          returnFocusRef={playbackReturnFocusRef}
          row={playback.row}
          videoMetricCoverageThreshold={playback.videoMetricCoverageThreshold}
        />
      ) : null}
    </section>
  );
}

export function materializeReportRequestFilters(
  filters: ReportFilterStateV1,
  now: Date,
): ReportFilterStateV1 {
  return {
    ...filters,
    date: {
      ...materializeReportFilterDateRange(filters.date, now),
      preset: "custom",
    },
  };
}

function systemNow(): Date {
  return new Date();
}

export function useUtcDayClock(now: () => Date): Date {
  const [clock, setClock] = useState(now);
  const day = clock.toISOString().slice(0, 10);
  useEffect(() => {
    const current = now();
    const nextUtcDay = Date.UTC(
      current.getUTCFullYear(),
      current.getUTCMonth(),
      current.getUTCDate() + 1,
    );
    const timer = window.setTimeout(
      () => setClock(now()),
      Math.max(1, nextUtcDay - current.valueOf() + 50),
    );
    return () => window.clearTimeout(timer);
  }, [day, now]);
  return clock;
}

function VideoDetailContextFallback({
  issue,
  onBack,
  selection,
}: {
  readonly issue: ReactNode;
  readonly onBack: () => void;
  readonly selection: VideoSelection;
}) {
  return (
    <section aria-label="Video detail" className="video-detail" role="region">
      <VideoDetailHeader label={selection.videoId} onBack={onBack} />
      {issue ?? (
        <Text aria-live="polite" role="status" size="sm">
          Preparing the report context for this video…
        </Text>
      )}
    </section>
  );
}

function ReportErrorPanel({
  onFiltersChange,
  onRetry,
  recovery,
}: {
  readonly onFiltersChange: (filters: ReportFilterStateV1) => boolean | void;
  readonly onRetry: () => void;
  readonly recovery: ReportErrorRecovery;
}) {
  return (
    <Alert aria-label={recovery.title} color="red" role="alert">
      <Stack gap="xs">
        <h2>{recovery.title}</h2>
        <Text size="sm">{recovery.description}</Text>
        <Button
          onClick={() => recovery.narrowedFilters === null
            ? onRetry()
            : onFiltersChange(recovery.narrowedFilters)}
          size="compact-sm"
          variant="light"
        >
          {recovery.actionLabel}
        </Button>
      </Stack>
    </Alert>
  );
}

interface ReportErrorRecovery {
  readonly actionLabel: "Retry report" | "Use a narrower range";
  readonly description: string;
  readonly narrowedFilters: ReportFilterStateV1 | null;
  readonly title: string;
}

function getReportErrorRecovery(
  error: unknown,
  filters: ReportFilterStateV1,
  now: Date,
): ReportErrorRecovery {
  const code = error instanceof ReportingApiError ? error.code : null;
  const candidate = narrowerReportFilters(filters, now);
  const narrowedFilters = candidate !== null
    && (code === "REPORTING_BUDGET_EXCEEDED" || code === "REPORTING_TIMEOUT")
    ? candidate
    : null;
  return {
    actionLabel: narrowedFilters === null ? "Retry report" : "Use a narrower range",
    description: narrowedFilters === null
      ? "The query failed; this is not an empty result."
      : "Try a shorter date range to reduce query work.",
    narrowedFilters,
    title: code === null
      ? "Report response could not be verified"
      : REPORT_ERROR_TITLES[code],
  };
}

export function narrowerReportFilters(
  filters: ReportFilterStateV1,
  now = new Date(),
): ReportFilterStateV1 | null {
  const range = materializeReportFilterDateRange(filters.date, now);
  const days = (Date.parse(`${range.to}T00:00:00.000Z`) - Date.parse(`${range.from}T00:00:00.000Z`)) / 86_400_000 + 1;
  if (days <= 1) return null;
  const nextDays = Math.max(1, Math.floor(days / 2));
  const from = new Date(`${range.to}T00:00:00.000Z`);
  from.setUTCDate(from.getUTCDate() - (nextDays - 1));
  return {
    ...filters,
    date: {
      from: from.toISOString().slice(0, 10),
      preset: "custom",
      to: range.to,
    },
  };
}

function Pagination({
  disabled,
  offset,
  onOffsetChange,
  rowCount,
  total,
}: {
  readonly disabled?: boolean;
  readonly offset: number;
  readonly onOffsetChange: (offset: number) => void;
  readonly rowCount: number;
  readonly total: number;
}) {
  const analytics = useAnalytics();
  const analyticsSessionId = useAnalyticsSessionId();
  if (total <= PAGE_SIZE) return null;
  const first = total === 0 ? 0 : offset + 1;
  const last = Math.min(total, offset + rowCount);
  const changePage = (nextOffset: number) => {
    onOffsetChange(nextOffset);
    if (!analyticsSessionId) return;
    const fromPage = Math.floor(offset / PAGE_SIZE) + 1;
    const toPage = Math.floor(nextOffset / PAGE_SIZE) + 1;
    analytics.capture(
      analyticsEvents.resultsPageChanged(analyticsSessionId, {
        direction: toPage > fromPage ? "next" : "previous",
        fromPage,
        pageSize: PAGE_SIZE,
        toPage,
      }),
    );
  };
  return (
    <Group justify="space-between">
      <Text c="dimmed" size="sm">Showing {first.toLocaleString("en-GB")}–{last.toLocaleString("en-GB")} of {total.toLocaleString("en-GB")}</Text>
      <Group gap="xs">
        <Button disabled={disabled || offset === 0} onClick={() => changePage(Math.max(0, offset - PAGE_SIZE))} variant="default">
          Previous page
        </Button>
        <Button disabled={disabled || offset + rowCount >= total} onClick={() => changePage(offset + PAGE_SIZE)} variant="default">
          Next page
        </Button>
      </Group>
    </Group>
  );
}
